import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDidMount, useLocalstorageState } from 'rooks';
import useSWR from 'swr';
import { isDeepEqual } from '@/helpers/comparison';
import { localSwrConfig, isSameListEntry } from '@/helpers/swr';
import { useSearchParam, addSearchParams } from '@/helpers/url';

// Subscribe to a local worker-calculation SWR cache but re-render only when the specific slice this
// consumer selects actually changes. The calculation workers replace the entire result object (new
// identity, new requestId) on every recompute, so a plain subscription re-renders every subscribing
// cell on every edit — the pricing/estimate grid typing-lag bottleneck. Passing a per-slice `compare`
// lets SWR skip cells whose numbers didn't change.
//
// `selector` MUST be null-safe: SWR calls it with `undefined` before the first result lands.
export const useLocalCalculationSlice = (key, selector) => {
  const { data } = useSWR(key, async () => {}, {
    ...localSwrConfig,
    compare: (a, b) => isDeepEqual(selector(a), selector(b))
  });

  return selector(data);
};

// The `compare` a `use<Entity>Object(uid)` hook subscribes to its list with, so it re-renders when its
// own entry changes and not on every change to the list (see `isSameListEntry`). The same lag fix as
// `useLocalCalculationSlice`, for fetched lists. Most Object hooks read a list someone above them already
// fetched and want `useListEntrySwrConfig` below; one whose list revalidates passes `{ compare }` alone.
//
// SWR keeps the `compare` it subscribed with for as long as the key does not change, so this one reads
// `uid` through a ref: a component handed a different uid on the same list still follows its new entry.
export const useListEntryCompare = (uid) => {
  const uidRef = useRef(uid);
  uidRef.current = uid;

  return useCallback((a, b) => isSameListEntry(a, b, uidRef.current), []);
};

// The cache-only read of a list (`localSwrConfig`), compared on `uid`'s entry.
export const useListEntrySwrConfig = (uid) => {
  const compare = useListEntryCompare(uid);

  return useMemo(() => ({ ...localSwrConfig, compare }), [compare]);
};

// Remembers a list page's sort selection (the `sort_by` / `sort_order` search params) in
// localStorage under `storageKey`, and restores it to the URL when the page is opened without
// one — so returning to the page comes back pre-sorted the way it was left.
//
// Returns the setter to call with the new `{ sort_by, sort_order }` whenever the user picks a
// sort. Restoring happens on mount only: a URL that already carries a sort (a shared link, the
// back button) wins, and clearing the sort mid-visit stays cleared until the next visit.
export const usePersistedSortParams = (storageKey) => {
  const [storedSortParams, setStoredSortParams] = useLocalstorageState(storageKey, null);
  const sortBy = useSearchParam('sort_by');
  const location = useLocation();
  const history = useHistory();

  useDidMount(() => {
    if (sortBy || !storedSortParams) {
      return;
    }
    history.replace(`${location.pathname}?${addSearchParams(storedSortParams)}`);
  });

  return setStoredSortParams;
};

const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(undefined);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return !!isMobile;
}
